import { Space, Typography } from 'antd';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { IntegrationField } from 'api/integrations';
import { LinkButton } from 'components/atoms/button';
import { BlockSpace } from 'components/atoms/space';
import { useTranslation } from 'hooks/use-translation';
import { RLProjectFieldMapping } from '../../../types';
import { PlaceholderText } from '../commons/styles';
import { ItemLayout } from '../layout';
import { CreateProps, StatusTypes } from '../use-automation-create';
import { DisplayDynamicValue } from './common';
import { ActionDrawer } from './edit-drawer';

const { Text } = Typography;

interface AutomationActionsTypes
  extends Omit<CreateProps, 'integrationId' | 'defautValue'> {
  data?: IntegrationField[];
  requiredFields?: IntegrationField[];
  apiStatus: StatusTypes;
}

export const AutomationActions = ({
  data = [],
  apiStatus,
  requiredFields = [],
  automationDetails,
  onChange,
}: AutomationActionsTypes): JSX.Element => {
  const [showModal, setShowModal] = useState(false);

  const title = useTranslation(
    'settings.integrations.sf.automation.create.withRl.title',
  );
  const SelectedFields = ({ actions }: { actions: RLProjectFieldMapping }) => {
    return (
      <Space direction="vertical">
        {Object.keys(actions).map((action) => {
          const fieldData = data.find(
            (datum) => datum.integrationFieldId === action,
          );
          const result = actions[action];

          if (result != null) {
            return fieldData?.fieldName === 'teamMembers'
              ? // only if value is there that field label will be displayed
                result.length > 0 && (
                  <Space>
                    <Text>{fieldData?.fieldLabel}</Text>:
                    {result?.map((item) => {
                      return (
                        <DisplayDynamicValue
                          valueStr={item.valueStr?.toString() ?? ''}
                          label={item.label?.toString() ?? ''}
                          boldText
                        />
                      );
                    })}
                  </Space>
                )
              : // only if value is there that field label will be displayed
                !!result?.label?.toString() && (
                  <Space>
                    <Text>{fieldData?.fieldLabel}</Text>
                    :
                    <DisplayDynamicValue
                      valueStr={result?.valueStr?.toString() ?? ''}
                      label={result?.label?.toString() ?? ''}
                      boldText
                    />
                  </Space>
                );
          }

          return null;
        })}
      </Space>
    );
  };

  const Content = () => (
    <BlockSpace direction="vertical" size={16}>
      {automationDetails?.rocketlaneFields ? (
        <SelectedFields actions={automationDetails.rocketlaneFields} />
      ) : (
        <PlaceholderText>
          <FormattedMessage
            id="settings.integrations.sf.automation.create.withRl.placeholder"
            values={{
              b: (chunks) => <b>{chunks}</b>,
            }}
          />
        </PlaceholderText>
      )}

      <LinkButton
        loading={apiStatus === 'loading'}
        onClick={() => setShowModal(true)}
        disabled={automationDetails?.salesforceObject == null}
        type="link"
      >
        <FormattedMessage id="edit" />
        ...
      </LinkButton>
    </BlockSpace>
  );

  const handleSave = (updatedFields: RLProjectFieldMapping) => {
    const rocketlaneFields = Object.keys(updatedFields).reduce((acc, key) => {
      return {
        ...acc,
        [key]:
          key !== 'teamMembers'
            ? {
                ...updatedFields[key],
                label:
                  updatedFields[key]?.valueMeta?.fieldLabel ??
                  updatedFields[key]?.label,
              }
            : updatedFields[key],
      };
    }, {});

    if (automationDetails) {
      onChange({
        ...automationDetails,
        rocketlaneFields,
      });
    }
    setShowModal(false);
  };

  return (
    <div>
      <ItemLayout right={<Content />} left={title} required />
      {automationDetails?.salesforceObject && showModal && (
        <ActionDrawer
          fields={requiredFields.length === 0 ? data : requiredFields}
          fieldsStatus={apiStatus}
          value={automationDetails?.rocketlaneFields}
          modalVisibility={showModal}
          handleModalVisibility={setShowModal}
          handleSelection={handleSave}
          salesforceObj={automationDetails?.salesforceObject}
        />
      )}
    </div>
  );
};
